import { Component, OnInit } from '@angular/core';
import { CommonService } from '../../services/common.service';
import { GlobalService } from '../../services/global.service';
import { SubstitutionService } from './substitution.service';
import { UpsertEmailDelegationRequest, DeleteEmailDelegationRequest, EmailDelegation, EmailDelegationView } from '../../models/substitution.models';
import { substitutionHeaders } from '../../common/staticGridColumnHeader';
import { ConstraintsModel, GridColumnHeader } from '../../models/grid-column-header';
import { GPID } from '../../models/GPID';
import { UserSystemPreference } from '../../models/userSystemPreference';
import { DateService } from '../../services/date.service';

@Component({
  selector: 'app-substitution',
  templateUrl: './substitution.component.html',
  styleUrl: './substitution.component.scss',
})

export class SubstitutionComponent implements OnInit {
  isDataAvailable: boolean = true;
  columns: GridColumnHeader[] = substitutionHeaders;
  emailDelegation: EmailDelegation[] = [];
  selectedEmailDelegation: EmailDelegation | null = null;
  isTableLoading: boolean = true;
  sortField: string = '';
  sortOrder: number = 1; // 1 for ascending, -1 for descending
  editingEmailDelegationRows: { [index: number]: boolean } = {};
  totalRecords: number = 0;
  clonedRows: { [index: number]: EmailDelegationView } = {};
  isAnyRowBeingEdited: boolean = false;
  isNewRowBeingEdited: boolean = false;
  pageNumber: number = 0;
  rowsOptions = [
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '75', value: 75 },
    { label: '100', value: 100 },
    { label: '200', value: 200 },
    { label: '300', value: 300 },
    { label: '500', value: 500 },
  ];
  rows: number = 25;
  dragSelecting = false;
  selectedIndexes: number[] = [];
  pagesToHoldData: number = 0;
  // Filtering
  filters: { [key: string]: any } = {};
  data: EmailDelegationView[] = [];
  validationTouched: boolean[] = [];
  companyCodeValues: { companycode: string }[] = [];
  gpids: GPID[] = [];
  filteredGpids: any[] = [];
  userPreferedDateformat!: string | '';
  userPreferedDateTimeformat!:string |null;
  userSystemPreference: UserSystemPreference[] = [];

  constructor(
    private readonly commonService: CommonService,
    private readonly substitutionService: SubstitutionService,
    private readonly globalVariable: GlobalService,
    private readonly dateService: DateService,
  ) {}

  ngOnInit(): void {
    this.loadData();
    this.getUserDropDownDetails();
    this.userSystemPreference = this.globalVariable.getUserPreferences;
    this.userSystemPreference.forEach((element) => {
      if (element.preferenceTitle == 'Date Format Display') {
        this.userPreferedDateTimeformat = element.dateFormatOptions[0].defaultFormat;
        this.userPreferedDateformat = element.dateFormatOptions[0].defaultFormat.split(' ')[0];
      }
    });
  }

  getUserDropDownDetails(): void {
    this.commonService
      .getReAssignUsers(this.globalVariable.getUserGPID, this.globalVariable.getSelectedSystemId)
      .subscribe((response) => {
        this.gpids = response.map((item: any) => ({
          value: item.emailId,
          name: item.fullName,
        }));

        this.filteredGpids = [...this.gpids];
        this.filteredGpids = this.filteredGpids.map((option) => ({
          ...option,
          value: option.value.toLowerCase(),
        }));
      });
  }

  filterGpids(event: any): void {
    const query = event.query.toLowerCase();
    this.filteredGpids = this.gpids.filter(
      (gpid) =>
        gpid.name.toLowerCase().includes(query) ||
        gpid.value.toLowerCase().includes(query),
    );
  }

  onRowsChange(event: Event) {
    this.selectedIndexes = [];
    this.syncSelectedWorkItems();
    const target = event.target as HTMLSelectElement;
    const selectedValue = Number(target.value);
    this.rows =
      selectedValue === 0 ? this.emailDelegation.length : selectedValue;
  }

  saveRow(index: number) {
    const row = this.emailDelegation[index];
    if (!this.validateRow(row)) {
      this.isAnyRowBeingEdited = true;
      this.validationTouched[index] = true;
      return;
    }
    delete this.editingEmailDelegationRows[index];
    this.upsertEmailDelegationDetails(row);
    this.isAnyRowBeingEdited = false;
  }

  validateRow(row: EmailDelegationView): boolean {
    return (
      !!row.fromEmail &&
      !!row.toEmail &&
      !!row.startDate &&
      !!row.comments
    );
  }

  addNewRow() {
    if (this.isAnyRowBeingEdited) return;
    const newRow: EmailDelegation = {
      fromEmail: '',
      toEmail: '',
      startDate: '',
      endDate: '',
      comments: '',
      isActive: true,
      isNew: true,
    };
    this.emailDelegation.unshift(newRow);
    this.editingEmailDelegationRows[0] = true;
    this.isAnyRowBeingEdited = true;
    this.isNewRowBeingEdited = true;
  }

  onDateSelect(event: any, rowData: any, field: string): void {
    const selectedDate = event;
    const formattedDate = this.formatDate(selectedDate);

    if (field === 'startDate') {
      rowData['startDate'] = formattedDate;
    } else if (field === 'endDate') {
      rowData['endDate'] = formattedDate;
    }
  }

  formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  upsertEmailDelegationDetails(emailDelegationDetails: EmailDelegationView): void {

    const formatDateString = (dateString: string | null, isEndDate: boolean = false) => {
      if (!dateString) return '';

      const [day, month, year] = dateString.split('/').map(Number);
      const date = new Date(year, month - 1, day);

      if (isNaN(date.getTime())) {
        return '';
      }

      if (isEndDate) {
        date.setHours(23, 59, 59, 0);
      }

      const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}T${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}.${date.getMilliseconds().toString().padStart(3, '0')}`;

      return formattedDate;
    };

    const payload: UpsertEmailDelegationRequest = {
      systemId: Number(this.globalVariable.getSelectedSystemId),
      userGpid: this.globalVariable.getUserGPID,
      fromEmail: emailDelegationDetails.fromEmail,
      toEmail: emailDelegationDetails.toEmail,
      comments: emailDelegationDetails.comments,
      startDate: formatDateString(emailDelegationDetails.startDate),
      endDate: formatDateString(emailDelegationDetails.endDate, true),
      isActive: true,
      isUserSubstitution: false,
    };
    this.substitutionService.upsertEmailDelegation(payload).subscribe({
      next: (response) => {
        if (response) {
          this.commonService.showMessage('Saved Email Delegation Detail.', 'success');
          this.loadData();
          this.isAnyRowBeingEdited = false;
        }
      },
      error: (error) => {
        this.commonService.showMessage(error.error.message, 'error');
      },
    });
  }

  cancelSave(index?: any): void {
    const row = this.emailDelegation[index];
    if (row.isNew) {
      this.emailDelegation.splice(index, 1);
      delete this.clonedRows[index];
      delete this.editingEmailDelegationRows[index];
      this.isAnyRowBeingEdited = false;
      this.isNewRowBeingEdited = false;
      return;
    }
    if (this.clonedRows[index]) {
      this.emailDelegation[index] = { ...this.clonedRows[index] };
      delete this.clonedRows[index];
    }
    delete this.editingEmailDelegationRows[index];
    this.isAnyRowBeingEdited = false;
    this.isNewRowBeingEdited = false;
  }

  editRow(index: number) {
    if (this.isAnyRowBeingEdited) return;
    this.clonedRows[index] = { ...this.emailDelegation[index] };
    this.editingEmailDelegationRows[index] = true;
    this.isAnyRowBeingEdited = true;
  }

  deleteRow(index: number) {
    if (this.isAnyRowBeingEdited || !this.selectedEmailDelegation) return;
    this.isAnyRowBeingEdited = true;
    const payload: DeleteEmailDelegationRequest = {
      fromEmail: this.selectedEmailDelegation.fromEmail,
      systemId: Number(this.globalVariable.getSelectedSystemId),
      userGpid: this.globalVariable.getUserGPID,
    };
    this.substitutionService.deleteEmailDelegation(payload).subscribe({
      next: (response) => {
        if (response) {
        this.isAnyRowBeingEdited = false;
        this.loadData();
        this.commonService.showMessage('Deleted Successfully.', 'success');
        }
      },
      error: (error) => {
        this.isAnyRowBeingEdited = false;
        this.commonService.showMessage(error.error.message, 'error');
      },
    });
  }

  onPageChange(event: any) {
    const currentPage = Math.ceil(event.first / event.rows) + 1;
    const rowsPerPage = event.rows;
    const totalPages = Math.ceil(this.totalRecords / rowsPerPage);
    const isLastPage =
      currentPage === totalPages - 1 || currentPage === totalPages;
    if (isLastPage) {
      this.pagesToHoldData = event.first;
    }
  }

  loadData(): void {
    this.isTableLoading = true;
    let pageNo = 0;
    this.substitutionService.getEmailDelegation({ systemId: Number(this.globalVariable.getSelectedSystemId), userGpid: this.globalVariable.getUserGPID, fromEmail: ''})
      .subscribe({
        next: (response) => {
          response = response.map((entry) => {
            return {
              ...entry,
              startDate: this.dateService.formatDateValue(entry.startDate, this.userPreferedDateformat),
              endDate: this.dateService.formatDateValue(entry.endDate, this.userPreferedDateformat),
            };
          });
          this.isDataAvailable = response.length > 0 ? true : false;
          this.totalRecords = response.length;
          const dataWorkItems = response.map((emailDelegation: EmailDelegationView) => ({
              ...emailDelegation,
              isNew: false,
            }),
          );
          this.emailDelegation = dataWorkItems;
          this.data = this.emailDelegation;
          this.isTableLoading = false;
          this.generateFilters();
          this.customFilter();
          setTimeout(() => {
            if (pageNo > 0) {
              this.pageNumber = this.pagesToHoldData;
            }
          }, 0);
        },
        error: (error) => {
          console.error('Error fetching email delegation:', error);
          this.commonService.showMessage(error.error.message, 'error');
          this.isDataAvailable = false;
          this.isTableLoading = true;
        },
  });
  }

  generateFilters() {
    this.columns.forEach((column) => {
      if (column.type == 'date') {
        column.filterOptions = [
          { label: 'Past 7 days', value: '7' },
          { label: 'Past 14 days', value: '14' },
          { label: 'Past 1 month', value: '30' },
          { label: 'Past 2 months', value: '60' },
          { label: 'Past 3 months', value: '90' },
          { label: 'Past 6 months', value: '182' },
          { label: 'Last year', value: '365' },
          { label: 'All', value: 'All' },
        ];
        column.selectedValues = [];
      } else {
        const values = new Set<string | boolean | number>();
        this.data.forEach((record) => {
          const propertyName: keyof EmailDelegationView =
            column.field as keyof EmailDelegationView;
          values.add(record[propertyName]);
        });
        column.filterOptions = Array.from(values).map((v) => ({
          label: v.toString(),
          value: v.toString(),
        }));
        column.selectedValues = [];
      }
    });
  }

  customClear(col: GridColumnHeader) {
    if (col.advancefilter.isAdvanceFilterUsed) {
      this.clearCall(col);
    }
    this.filters[col.field] = [];
    this.customFilter(col.field);
  }

  isSelected(index: number): boolean {
    return this.selectedIndexes.includes(index);
  }

  onRowClick(event: MouseEvent, index: number): void {
    this.toggleRowSelection(index, event.ctrlKey || event.metaKey, event.shiftKey);
  }

  toggleRowSelection(index: number, ctrlKey: boolean, shiftKey: boolean): void {
    if (!ctrlKey && !shiftKey) {
      this.selectedIndexes = [index];
    } else {
      const alreadySelected = this.selectedIndexes.includes(index);
      if (!alreadySelected) {
        this.selectedIndexes.push(index);
      } else {
        this.selectedIndexes = this.selectedIndexes.filter((i) => i !== index);
      }
    }
    this.syncSelectedWorkItems();
  }

  onMouseDown(event: MouseEvent, index: number) {
    const ctrlKey = event.ctrlKey || event.metaKey;
    const shiftKey = event.shiftKey;
    if (event.button === 0 && !ctrlKey && !shiftKey) {
      this.dragSelecting = true;
      this.selectedIndexes = [index];
      this.syncSelectedWorkItems();
      event.preventDefault();
    }
  }

  onMouseOver(event: MouseEvent, index: number) {
    if (
      event.button === 0 &&
      this.dragSelecting &&
      !this.selectedIndexes.includes(index)
    ) {
      this.selectedIndexes.push(index);
      this.syncSelectedWorkItems();
    }
  }

  onMouseUp() {
    if (this.dragSelecting) {
      this.dragSelecting = false;
    }
  }

  onRowKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleRowSelection(index, event.ctrlKey || event.metaKey, event.shiftKey);
    }
  }

  onRowFocus(_event: FocusEvent, _index: number): void {
  }

  syncSelectedWorkItems() {
    const validIndex = this.selectedIndexes.find(
      (i) => i >= 0 && i < this.emailDelegation.length,
    );
    this.selectedEmailDelegation =
      validIndex !== undefined ? this.emailDelegation[validIndex] : null;
  }

  customFilter(value?: any) {
    this.selectedIndexes = [];
    this.syncSelectedWorkItems();
    let filteredData = [...this.data];
    if (Object.keys(this.filters).length) {
      Object.keys(this.filters).forEach((field) => {
        const selectedValues = this.filters[field];
        if (!selectedValues || selectedValues.length === 0)
          return;

        if (value === 'date') {
          if (selectedValues.includes('All'))
            return;

          const today = new Date();
          filteredData = filteredData.filter((data) => {
            const rawValue = data[field as keyof EmailDelegationView];
            if (!rawValue)
              return false;

            const itemDate = this.parseCustomDate(rawValue as any);

            if (!itemDate)
              return false;

            if (isNaN(itemDate.getTime()))
              return false;

            return selectedValues.some((selectedRange: any) => {
              if (selectedRange === '365') {
                const start = new Date(today.getFullYear() - 1, 0, 1);
                const end = new Date(today.getFullYear(), 0, 1);
                return itemDate >= start && itemDate < end;
              }

              let filterDate = new Date(today);
              switch (selectedRange) {
                case '7':
                  filterDate.setDate(today.getDate() - 7);
                  break;
                case '14':
                  filterDate.setDate(today.getDate() - 14);
                  break;
                case '30':
                  filterDate.setMonth(today.getMonth() - 1);
                  break;
                case '60':
                  filterDate.setMonth(today.getMonth() - 2);
                  break;
                case '90':
                  filterDate.setMonth(today.getMonth() - 3);
                  break;
                case '182':
                  filterDate.setMonth(today.getMonth() - 6);
                  break;
                default:
                  return false;
              }
              return itemDate >= filterDate;
            });
          });
        } else {
          const propertyName: keyof EmailDelegationView = field as keyof EmailDelegationView;
          filteredData = filteredData.filter((substitutionData) => selectedValues.includes(substitutionData[propertyName]));
        }
      });
    }
    this.emailDelegation = filteredData;
  }

  parseCustomDate(dateString: string): Date | null {
		if (!dateString?.trim())
			return null;

		const [datePart, timePart] = dateString.trim().split(/\s+/);
		const format = this.userPreferedDateTimeformat;

		if (!format)
			return null;

		const dateSegments = datePart.split(/[-/]/).map(Number);
		if (dateSegments.length !== 3 || dateSegments.some(isNaN)) {
			return null;
		}

		let day: number, month: number, year: number;

		if (format.startsWith('dd/MM')) {
			[day, month, year] = dateSegments;
		} else if (format.startsWith('MM/dd')) {
			[month, day, year] = dateSegments;
		} else if (format.startsWith('yyyy/MM')) {
			[year, month, day] = dateSegments;
		} else {
			return null;
		}

		let hours = 0, minutes = 0, seconds = 0;

		if (timePart) {
			const t = timePart.split(':').map(Number);
			if (t.some(isNaN))
				return null;
			hours = t[0] ?? 0;
			minutes = t[1] ?? 0;
			seconds = t[2] ?? 0;
			if (hours > 23 || minutes > 59 || seconds > 59 || hours < 0 || minutes < 0 || seconds < 0) {
				return null;
			}
		}

		const date = new Date(year, month - 1, day, hours, minutes, seconds);
		return isNaN(date.getTime()) ? null : date;
	} 

  applyCall(col: GridColumnHeader, filterRef: any) {
    col.advancefilter.isAdvanceFilterUsed = true;
    this.applyAdvancedFilter(col);
    filterRef.hide();
  }

  applyAdvancedFilter(col: GridColumnHeader) {
    const field = col.field;
    const filterMeta = col.advancefilter;
    // Skip filtering if no constraints or filter not enabled
    if (!filterMeta?.isAdvanceFilterUsed || !filterMeta.constraints?.length) {
      return;
    }
    const constraints = filterMeta.constraints;
    const lastConstraint = constraints[constraints.length - 1];
    const operator =
      !lastConstraint ||
      lastConstraint.value === null ||
      lastConstraint.value === ''
        ? 'or'
        : filterMeta.operator || 'or';
    this.emailDelegation = this.emailDelegation.filter((row) => {
      const rowValue = (row as any)[field];
      const results = constraints.map((constraint) => {
        const filterValue = constraint.value;
        const matchMode = constraint.matchMode;
        const stringValue = rowValue?.toString()?.toLowerCase?.();
        const filterString = filterValue?.toString()?.toLowerCase?.();
        switch (matchMode) {
          case 'eq':
            return stringValue === filterString;
          case 'startswith':
            return stringValue?.startsWith(filterString);
          case 'contains':
            return stringValue?.includes(filterString);
          case 'doesnotcontain':
            return !stringValue?.includes(filterString);
          case 'endswith':
            return stringValue?.endsWith(filterString);
          case 'gt':
            return new Date(rowValue) > new Date(filterValue);
          case 'gte':
            return new Date(rowValue) >= new Date(filterValue);
          case 'lt':
            return new Date(rowValue) < new Date(filterValue);
          case 'lte':
            return new Date(rowValue) <= new Date(filterValue);
          case 'isnull':
            return rowValue == null || rowValue === '';
          case 'isnotnull':
            return rowValue != null && rowValue !== '';
          default:
            return true;
        }
      });
      // Combine with AND/OR operator
      return operator === 'or'
        ? results.some((r) => r)
        : results.every((r) => r);
    });
  }

  clearCall(col: GridColumnHeader, filterCallback?: any, filterRef?: any) {
    col.advancefilter.isAdvanceFilterUsed = false;
    col.advancefilter.constraints.forEach((c: ConstraintsModel) => {
      c.value = '';
    });
    this.emailDelegation = this.data;
    if (filterCallback && filterRef) {
      filterCallback(null);
      filterRef.hide();
    }
  }

  onDateRuleChange(event: any, field: string, index: number) {
    const value = event ? event : event.target?.value;
    const col = this.columns.find((c) => c.field === field);
    if (col) {
      col.advancefilter.constraints[index].value = value;
    }
  }

  onRuleChange(event: any, field: string, index: number) {
    const value = typeof event === 'string' ? event : event.target?.value;
    const col = this.columns.find((c) => c.field === field);
    if (col) {
      col.advancefilter.constraints[index].value = value;
    }
  }

  onMatchModeChange(event: any, field: string, index: number) {
    const mode = typeof event === 'string' ? event : event.target?.value;
    const col = this.columns.find((c) => c.field === field);
    if (col) {
      col.advancefilter.constraints[index].matchMode = mode;
    }
  }

  onOperatorChange(event: any, field: string) {
    const operator = typeof event === 'string' ? event : event.value;
    const col = this.columns.find((c) => c.field === field);
    if (col) {
      col.advancefilter.operator = operator;
    }
  }
}
